import { api, publicApi, tokenStorage } from '@/api/http'
import type { LoginResponse, User } from '@/api/types'

/**
 * Stav prihlaseni mimo React.
 *
 * Router se na nej pta v `beforeLoad`, tedy drive, nez se cokoli vykresli —
 * proto to neni React context, ale obycejny objekt s odberateli. Komponenty
 * se na nej napoji pres useAuth().
 */
export interface AuthState {
  token: string | null
  user: User | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  register: (customerName: string, email: string, password: string) => Promise<void>
  logout: () => void
  /** Dotahne uzivatele k tokenu ze storage (po tvrdem nacteni stranky). */
  restore: () => Promise<void>
}

type Listener = () => void

const listeners = new Set<Listener>()

function notify() {
  listeners.forEach((l) => l())
}

export const auth: AuthState = {
  token: tokenStorage.get(),
  user: null,
  get isAuthenticated() {
    return this.token !== null
  },

  async login(email, password) {
    const { data } = await publicApi.post<LoginResponse>('/auth/login', { email, password })
    applySession(data)
  },

  async register(customerName, email, password) {
    const { data } = await publicApi.post<LoginResponse>('/auth/register', {
      customerName,
      email,
      password,
    })
    applySession(data)
  },

  logout() {
    tokenStorage.clear()
    auth.token = null
    auth.user = null
    notify()
  },

  async restore() {
    const token = tokenStorage.get()
    if (!token) return
    auth.token = token
    try {
      const { data } = await api.get<User>('/auth/me')
      auth.user = data
    } catch {
      // Neplatny nebo expirovany token — 401 interceptor uz ho zahodil,
      // tady jen srovname stav v pameti.
      auth.token = null
      auth.user = null
    }
    notify()
  },
}

function applySession(session: LoginResponse) {
  tokenStorage.set(session.token)
  auth.token = session.token
  auth.user = session.user
  notify()
}

export function subscribeAuth(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
